<template>
  <!-- Container styled to match the medias list table card —
       same rounded-xl + border-gray-200/60 + card-shadow combo so
       this card sits visually consistent with the rest of the
       app's data tables. The shared `table-scroll` helper isn't used
       here on purpose: the Top 10 list is at most 10 rows so it
       never needs a scroll container, and `table-scroll` adds a
       `position: sticky` thead that hooks to the page scroll on
       short tables and floats over the page header. -->
  <div class="bg-white rounded-xl border border-gray-200/60 card-shadow overflow-hidden">
    <!-- Card header — title + tab nav. Padding matches the
         medias list page's table header bar so the card reads as
         part of the same UI family. -->
    <div class="px-6 py-3.5 border-b border-gray-100 flex items-start justify-between gap-3 flex-wrap">
      <div class="min-w-0">
        <h2 class="text-[15px] font-semibold text-gray-900">Top Content</h2>
        <p class="text-sm text-gray-400 mt-0.5">Top 10 by sessions in the selected period</p>
      </div>
      <div class="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
        <button
          v-for="t in visibleTabs"
          :key="t.key"
          @click="active = t.key"
          :class="[
            'h-[28px] px-3 rounded-md text-[12px] font-medium transition-all duration-150 cursor-pointer',
            active === t.key
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          ]"
        >{{ t.label }}</button>
      </div>
    </div>

    <table class="w-full">
      <thead>
        <tr class="bg-gray-50 border-b border-gray-100">
          <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 w-[60px]">#</th>
          <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Title</th>
          <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">
            <span class="inline-flex items-center gap-1 justify-end align-middle">
              <span>Sessions</span>
              <ColumnInfo
                col-key="sessions"
                :active="activeHeader === 'sessions'"
                @toggle="toggleHeader"
                info="Sessions in the window for this row. A session opens on the first play intent and closes on SDK destroy or a 30-minute continuous-pause timeout."
              />
            </span>
          </th>
          <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden sm:table-cell">
            <span class="inline-flex items-center gap-1 justify-end align-middle">
              <span>Session Duration</span>
              <ColumnInfo
                col-key="sessionDuration"
                :active="activeHeader === 'sessionDuration'"
                @toggle="toggleHeader"
                info="Total wall-clock across sessions for this row — playing time (content + ads) plus paused time."
              />
            </span>
          </th>
          <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden lg:table-cell">
            <span class="inline-flex items-center gap-1 justify-end align-middle">
              <span>Avg. Session Duration</span>
              <ColumnInfo
                col-key="avgSessionDuration"
                :active="activeHeader === 'avgSessionDuration'"
                @toggle="toggleHeader"
                info="Session Duration ÷ Sessions for this row."
              />
            </span>
          </th>
          <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell">
            <span class="inline-flex items-center gap-1 justify-end align-middle">
              <span>Watch Time</span>
              <ColumnInfo
                col-key="watchTime"
                :active="activeHeader === 'watchTime'"
                @toggle="toggleHeader"
                info="Total time the media element was playing content across sessions for this row. Excludes ads (tracked separately) and paused / buffering periods."
              />
            </span>
          </th>
          <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden lg:table-cell">
            <span class="inline-flex items-center gap-1 justify-end align-middle">
              <span>Avg. Watch Time</span>
              <ColumnInfo
                col-key="avgWatchTime"
                :active="activeHeader === 'avgWatchTime'"
                @toggle="toggleHeader"
                info="Watch Time ÷ Sessions for this row."
              />
            </span>
          </th>
        </tr>
      </thead>

      <!-- Skeleton rows during load — matches the medias list
           skeleton-pulse pattern so loading states are consistent
           across the app. -->
      <tbody v-if="loading">
        <tr v-for="i in 5" :key="'skel-' + i" class="border-b border-gray-50 last:border-b-0">
          <td class="px-6 py-4"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-6" /></td>
          <td class="px-6 py-4"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse" :style="{ width: (40 + i * 5) + '%' }" /></td>
          <td class="px-6 py-4"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-16 ml-auto" /></td>
          <td class="px-6 py-4 hidden sm:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-16 ml-auto" /></td>
          <td class="px-6 py-4 hidden lg:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-14 ml-auto" /></td>
          <td class="px-6 py-4 hidden md:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-16 ml-auto" /></td>
          <td class="px-6 py-4 hidden lg:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-14 ml-auto" /></td>
        </tr>
      </tbody>

      <!-- Real rows -->
      <tbody v-else-if="rows.length" class="divide-y divide-gray-50">
        <tr
          v-for="(row, i) in rows"
          :key="row.id"
          class="hover:bg-gray-50/50 transition-colors"
        >
          <td class="px-6 py-4">
            <span class="text-sm font-data text-gray-400">{{ i + 1 }}</span>
          </td>
          <td class="px-6 py-4 max-w-[300px]">
            <span class="text-[14px] text-gray-900 truncate block">{{ row.title || 'Untitled' }}</span>
          </td>
          <td class="px-6 py-4 text-right">
            <span class="text-sm font-data text-gray-900">{{ formatNumber(row.count) }}</span>
          </td>
          <td class="px-6 py-4 text-right hidden sm:table-cell">
            <span class="text-sm font-data text-gray-500">{{ formatDurationSeconds(row.totalSessionDuration) }}</span>
          </td>
          <td class="px-6 py-4 text-right hidden lg:table-cell">
            <span class="text-sm font-data text-gray-500">{{ formatDurationSeconds(row.avgSessionDuration) }}</span>
          </td>
          <td class="px-6 py-4 text-right hidden md:table-cell">
            <span class="text-sm font-data text-gray-500">{{ formatDurationSeconds(row.totalTime) }}</span>
          </td>
          <td class="px-6 py-4 text-right hidden lg:table-cell">
            <span class="text-sm font-data text-gray-500">{{ formatDurationSeconds(row.avgTime) }}</span>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Empty state — same vertical-pad / icon-circle treatment as
         the medias list's "no medias yet" empty state, so the
         in-card empty matches the section-level empty visually. -->
    <div v-if="!loading && !rows.length" class="px-6 py-12 text-center">
      <div class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
        <svg class="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
        </svg>
      </div>
      <p class="text-sm text-gray-500">{{ emptyLabel }}</p>
    </div>
  </div>
</template>

<script>
import ColumnInfo from './ColumnInfo.vue'

/**
 * Top Content card. Wraps four ranked-list tabs in a single card so
 * the Overview view doesn't have to render four separate cards
 * vying for attention. Each list comes pre-shaped from the API as
 * `[{ id, title, stream, totalTime, avgTime, count }]` sorted by
 * `totalTime` descending — the component is purely presentational.
 *
 * The "Stream" column auto-hides when the active tab's list never
 * carries a stream value (Lives / Medias / Categories). Only Shows
 * populates it, so the column shows up just for that tab. Keeps the
 * other three tabs cleaner without forcing a per-tab template.
 */
export default {
  name: 'TopContentCard',
  components: { ColumnInfo },
  props: {
    loading: { type: Boolean, default: false },
    // Expected shape: { lives, shows, medias, categories } — each an
    // array of `{ id, title, stream, totalTime, avgTime, count }`.
    data: { type: Object, default: () => ({}) },
    // Explicit allow-list of tab keys to render. Default shows all
    // four; per-view callers narrow to the relevant subset:
    //   Overview     → all four
    //   Lives view   → ['lives', 'shows', 'categories']
    //   Medias view  → ['medias', 'categories']
    // The component still auto-hides any allowed tab whose data is
    // empty, so empty tabs never render even if listed here.
    tabs: {
      type: Array,
      default: () => ['lives', 'shows', 'medias', 'categories']
    }
  },
  data() {
    return {
      active: 'lives',
      allTabs: [
        { key: 'lives',      label: 'Lives' },
        { key: 'shows',      label: 'Shows' },
        { key: 'medias',     label: 'Medias' },
        { key: 'categories', label: 'Categories' }
      ],
      activeHeader: null
    }
  },
  mounted() {
    this._onDocClick = () => { this.activeHeader = null }
    document.addEventListener('click', this._onDocClick)
  },
  beforeUnmount() {
    if (this._onDocClick) document.removeEventListener('click', this._onDocClick)
  },
  watch: {
    // If the active tab is empty (e.g. on a per-media view where
    // Lives has no data), fall through to the first visible tab so
    // the table doesn't render an empty state with no pill selected.
    visibleTabs: {
      immediate: true,
      handler(tabs) {
        if (!tabs.length) return
        if (!tabs.some(t => t.key === this.active)) {
          this.active = tabs[0].key
        }
      }
    }
  },
  computed: {
    rows() {
      const list = this.data?.[this.active]
      return Array.isArray(list) ? list : []
    },
    /**
     * Two layers of filtering:
     *   1. The `tabs` prop is an allow-list of keys the parent
     *      explicitly permits (e.g. Lives view forbids "medias").
     *   2. Tabs with no data still get hidden so a specific-entity
     *      view doesn't render dead pills.
     */
    visibleTabs() {
      const allowed = new Set(this.tabs)
      return this.allTabs
        .filter(t => allowed.has(t.key))
        .filter(t => {
          const list = this.data?.[t.key]
          return Array.isArray(list) && list.length > 0
        })
    },
    emptyLabel() {
      const labels = {
        lives:      'No live data for this period',
        shows:      'No show data for this period',
        medias:     'No media data for this period',
        categories: 'No category data for this period'
      }
      return labels[this.active] || 'No data'
    }
  },
  methods: {
    /**
     * Toggle the per-column info popover. Click-twice closes the same
     * key; switching keys swaps; null closes everything.
     */
    toggleHeader(key) {
      this.activeHeader = this.activeHeader === key ? null : key
    },
    /**
     * Format an integer seconds value as `Xh Ym`, `Mm Ss`, or `Ss`.
     * Mirrors the Lives tab's formatter so duration formatting is
     * consistent across the analytics views.
     */
    formatDurationSeconds(s) {
      if (!s) return '0s'
      const sec = Math.floor(s)
      const m = Math.floor(sec / 60)
      const h = Math.floor(m / 60)
      if (h > 0) return `${h}h ${m % 60}m`
      if (m > 0) return `${m}m ${sec % 60}s`
      return `${sec}s`
    },
    formatNumber(n) {
      return (n || 0).toLocaleString()
    }
  }
}
</script>
